import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Truck, Package, LogOut, ChevronRight, MapPin, RefreshCw, CloudOff } from 'lucide-react'
import { motApi, getMotInfo, logoutMotorista, flushOutbox, outboxCount } from '@/lib/motoristaApi'
import { formatMoney } from '@/lib/api'

const STATUS_LABEL: Record<string, { txt: string; cls: string }> = {
  faturada: { txt: 'Pronta p/ sair', cls: 'bg-amber-100 text-amber-700' },
  em_rota: { txt: 'Em rota', cls: 'bg-blue-100 text-blue-700' },
  em_acerto: { txt: 'Aguardando acerto', cls: 'bg-violet-100 text-violet-700' },
}

export default function EntregadorCargas() {
  const nav = useNavigate()
  const info = getMotInfo()
  const [pend, setPend] = useState(outboxCount())
  const [online, setOnline] = useState(navigator.onLine)
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['mot-cargas'],
    queryFn: async () => (await motApi.get('/cargas')).data as any[],
    refetchInterval: 60000,
  })

  useEffect(() => {
    const sync = async () => { await flushOutbox(); setPend(outboxCount()); refetch() }
    sync()
    const on = () => { setOnline(true); sync() }
    const off = () => setOnline(false)
    window.addEventListener('online', on); window.addEventListener('offline', off)
    const t = setInterval(() => setPend(outboxCount()), 4000)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); clearInterval(t) }
  }, [])

  return (
    <div className="min-h-screen bg-ink-50 pb-10">
      <header className="sticky top-0 z-10 bg-emerald-700 text-white px-4 h-14 flex items-center justify-between shadow">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6" />
          <div>
            <div className="font-bold leading-tight">Minhas Cargas</div>
            <div className="text-[11px] text-white/70 leading-tight">{info?.nome}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(!online || pend > 0) && (
            <span className="flex items-center gap-1 text-[11px] font-bold bg-white/15 rounded-full px-2 py-1">
              <CloudOff className="h-4 w-4" />{pend > 0 ? pend : 'off'}
            </span>
          )}
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-white/10">
            <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={logoutMotorista} className="p-2 rounded-lg hover:bg-white/10"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="p-4 space-y-3 max-w-lg mx-auto">
        {isLoading && <div className="text-center text-ink-400 py-10">Carregando…</div>}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <div className="text-center text-ink-400 py-16">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
            Nenhuma carga atribuída a você no momento.
          </div>
        )}
        {data?.map((c) => {
          const st = STATUS_LABEL[c.status] || { txt: c.status, cls: 'bg-ink-100 text-ink-600' }
          const pend = c.paradas - c.confirmadas
          return (
            <button key={c.id} onClick={() => nav(`/m/entregador/carga/${c.id}`)}
              className="w-full text-left bg-white rounded-2xl shadow-sm border border-ink-100 p-4 active:scale-[0.99] transition">
              <div className="flex items-center justify-between">
                <div className="font-extrabold text-lg text-ink-800">Carga #{c.numero}</div>
                <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${st.cls}`}>{st.txt}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-ink-500">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{c.paradas} paradas</span>
                {c.veiculo && <span className="flex items-center gap-1"><Truck className="h-4 w-4" />{c.veiculo}</span>}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-xs text-ink-400">A receber</div>
                  <div className="font-bold text-ink-800">{formatMoney(c.total)}</div>
                </div>
                <div className="text-right">
                  {pend > 0
                    ? <span className="text-sm font-semibold text-amber-600">{pend} pendente{pend > 1 ? 's' : ''}</span>
                    : <span className="text-sm font-semibold text-emerald-600">✓ Concluída</span>}
                  <div className="text-emerald-600 flex items-center justify-end mt-1"><ChevronRight className="h-5 w-5" /></div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
